import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import {
  Search, Plus, FileText, Edit2, Trash2, Clock,
  DollarSign, Package, ChevronDown, ChevronUp, Loader, X, Copy, Layers
} from 'lucide-react';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';

export default function JobTemplatesPage({ companyId, onNavigate }) {
  const { colors } = useTheme();
  const { showAlert, showConfirm, showSuccess, showError } = useModal();

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    estimated_duration: 60,
    estimated_labor_cost: 0,
    estimated_materials_cost: 0
  });
  const [templateItems, setTemplateItems] = useState([]);

  // Inventory items for adding to template
  const [inventoryItems, setInventoryItems] = useState([]);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [itemSearch, setItemSearch] = useState('');

  // Template picker for nested templates
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (companyId) {
      loadTemplates();
      loadInventoryItems();
    } else {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    filterTemplates();
  }, [templates, searchTerm, categoryFilter]);

  const loadTemplates = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabaseData
        .from('job_templates')
        .select(`
          *,
          job_template_items(*)
        `)
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      setTemplates(data || []);

      // Extract categories
      const uniqueCats = [...new Set((data || []).map(t => t.category).filter(Boolean))];
      setCategories(uniqueCats);

    } catch (error) {
      console.error('Error loading templates:', error);
      showError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadInventoryItems = async () => {
    try {
      const { data } = await supabaseData
        .from('inventory_items')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      setInventoryItems(data || []);
    } catch (error) {
      console.error('Error loading inventory:', error);
    }
  };

  const filterTemplates = () => {
    let filtered = [...templates];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.name?.toLowerCase().includes(term) ||
        t.description?.toLowerCase().includes(term)
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(t => t.category === categoryFilter);
    }

    setFilteredTemplates(filtered);
  };

  const openCreateModal = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      estimated_duration: 60,
      estimated_labor_cost: 0,
      estimated_materials_cost: 0
    });
    setTemplateItems([]);
    setShowModal(true);
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name || '',
      description: template.description || '',
      category: template.category || '',
      estimated_duration: template.estimated_duration || 60,
      estimated_labor_cost: template.estimated_labor_cost || 0,
      estimated_materials_cost: template.estimated_materials_cost || 0
    });
    setTemplateItems(template.job_template_items || []);
    setShowModal(true);
  };

  const handleDuplicate = async (template) => {
    try {
      // Create new template
      const { data: newTemplate, error } = await supabaseData
        .from('job_templates')
        .insert({
          company_id: companyId,
          name: template.name + ' (Copy)',
          description: template.description,
          category: template.category,
          estimated_duration: template.estimated_duration,
          estimated_labor_cost: template.estimated_labor_cost,
          estimated_materials_cost: template.estimated_materials_cost
        })
        .select()
        .single();

      if (error) throw error;

      // Copy template items
      if (template.job_template_items?.length > 0) {
        const items = template.job_template_items.map(item => ({
          template_id: newTemplate.id,
          inventory_item_id: item.inventory_item_id,
          sku_id: item.sku_id,
          name: item.name,
          quantity: item.quantity,
          unit_cost: item.unit_cost,
          is_optional: item.is_optional,
          notes: item.notes,
          sort_order: item.sort_order
        }));

        await supabaseData.from('job_template_items').insert(items);
      }

      showSuccess('Template duplicated');
      loadTemplates();
    } catch (error) {
      showError('Failed to duplicate template');
    }
  };

  const handleDelete = async (template) => {
    const confirmed = await showConfirm(
      `Delete template "${template.name}"?`,
      { confirmStyle: 'danger' }
    );

    if (!confirmed) return;

    try {
      await supabaseData
        .from('job_templates')
        .update({ is_active: false })
        .eq('id', template.id);

      showSuccess('Template deleted');
      loadTemplates();
    } catch (error) {
      showError('Failed to delete template');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showError('Name is required');
      return;
    }

    setSaving(true);
    try {
      let templateId;

      if (editingTemplate) {
        // Update template
        const { error } = await supabaseData
          .from('job_templates')
          .update({
            name: formData.name.trim(),
            description: formData.description.trim(),
            category: formData.category.trim() || null,
            estimated_duration: parseInt(formData.estimated_duration) || 60,
            estimated_labor_cost: parseFloat(formData.estimated_labor_cost) || 0,
            estimated_materials_cost: parseFloat(formData.estimated_materials_cost) || 0,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingTemplate.id);

        if (error) throw error;
        templateId = editingTemplate.id;

        // Delete existing items
        await supabaseData
          .from('job_template_items')
          .delete()
          .eq('template_id', templateId);

      } else {
        // Create template
        const { data, error } = await supabaseData
          .from('job_templates')
          .insert({
            company_id: companyId,
            name: formData.name.trim(),
            description: formData.description.trim(),
            category: formData.category.trim() || null,
            estimated_duration: parseInt(formData.estimated_duration) || 60,
            estimated_labor_cost: parseFloat(formData.estimated_labor_cost) || 0,
            estimated_materials_cost: parseFloat(formData.estimated_materials_cost) || 0
          })
          .select()
          .single();

        if (error) throw error;
        templateId = data.id;
      }

      // Insert template items
      if (templateItems.length > 0) {
        const items = templateItems.map((item, index) => ({
          template_id: templateId,
          inventory_item_id: item.inventory_item_id || null,
          sku_id: item.sku_id || null,
          name: item.name,
          quantity: item.quantity || 1,
          unit_cost: item.unit_cost || 0,
          is_optional: item.is_optional || false,
          notes: item.notes || null,
          sort_order: index
        }));

        await supabaseData.from('job_template_items').insert(items);
      }

      showSuccess(editingTemplate ? 'Template updated' : 'Template created');
      setShowModal(false);
      loadTemplates();

    } catch (error) {
      console.error('Error saving template:', error);
      showError('Failed to save template: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const addItemToTemplate = (inventoryItem) => {
    setTemplateItems(prev => [...prev, {
      id: `temp-${Date.now()}`,
      inventory_item_id: inventoryItem.id,
      name: inventoryItem.name,
      quantity: 1,
      unit_cost: inventoryItem.unit_cost || 0,
      is_optional: false
    }]);
    setShowItemPicker(false);
    setItemSearch('');
  };

  const addManualItem = () => {
    setTemplateItems(prev => [...prev, {
      id: `temp-${Date.now()}`,
      inventory_item_id: null,
      name: '',
      quantity: 1,
      unit_cost: 0,
      is_optional: false
    }]);
  };

  const updateTemplateItem = (index, field, value) => {
    setTemplateItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeTemplateItem = (index) => {
    setTemplateItems(prev => prev.filter((_, i) => i !== index));
  };

  // Include another template's items (nested templates)
  const includeTemplate = (template) => {
    if (!template.job_template_items?.length) {
      setShowTemplatePicker(false);
      return;
    }

    // Add all items from the included template
    const newItems = template.job_template_items.map(item => ({
      id: `temp-${Date.now()}-${Math.random()}`,
      inventory_item_id: item.inventory_item_id,
      name: item.name,
      quantity: item.quantity,
      unit_cost: item.unit_cost,
      is_optional: item.is_optional,
      notes: item.notes,
      included_from_template: template.name // Track where it came from
    }));

    setTemplateItems(prev => [...prev, ...newItems]);
    setShowTemplatePicker(false);
    setTemplateSearch('');
  };

  const filteredInventoryItems = inventoryItems.filter(item =>
    item.name?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  // Filter templates for the template picker (exclude the one being edited)
  const filteredTemplatesForPicker = templates.filter(t => {
    // Exclude the template being edited
    if (editingTemplate && t.id === editingTemplate.id) return false;
    // Must have items
    if (!t.job_template_items?.length) return false;
    // Search filter
    if (templateSearch && !t.name.toLowerCase().includes(templateSearch.toLowerCase())) return false;
    return true;
  });

  const commonCategories = [
    'Installation',
    'Repair',
    'Maintenance',
    'Inspection',
    'Emergency',
    'Upgrade',
    'Other'
  ];

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '50vh',
        color: colors.textMuted
      }}>
        <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '1rem' }}>Loading templates...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div>
          <h1 style={{ margin: 0, color: colors.text, fontSize: isMobile ? '1.5rem' : '2rem' }}>
            <FileText size={isMobile ? 24 : 32} style={{ marginRight: '0.75rem', verticalAlign: 'middle' }} />
            Job Templates
          </h1>
          <p style={{ margin: '0.5rem 0 0', color: colors.textMuted }}>
            Pre-built job plans with materials lists
          </p>
        </div>
        <button
          onClick={openCreateModal}
          style={{
            padding: '0.75rem 1.5rem',
            background: colors.accent,
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            justifyContent: 'center'
          }}
        >
          <Plus size={20} />
          Create Template
        </button>
      </div>

      {/* Search and Filters */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: colors.textMuted
          }} />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem 0.75rem 2.75rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              fontSize: '1rem'
            }}
          />
        </div>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            background: colors.bgInput,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            color: colors.text,
            minWidth: '150px'
          }}
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Templates List */}
      {filteredTemplates.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: colors.textMuted
        }}>
          <FileText size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <p style={{ margin: 0 }}>
            {templates.length === 0 ? 'No templates yet' : 'No templates match your search'}
          </p>
          {templates.length === 0 && (
            <button
              onClick={openCreateModal}
              style={{
                marginTop: '1rem',
                padding: '0.75rem 1.5rem',
                background: colors.accent,
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Create Your First Template
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filteredTemplates.map(template => {
            const isExpanded = expandedId === template.id;
            const itemCount = template.job_template_items?.length || 0;
            const totalMaterialsCost = (template.job_template_items || []).reduce(
              (sum, item) => sum + (item.quantity * item.unit_cost), 0
            );

            return (
              <div
                key={template.id}
                style={{
                  background: colors.bgCard,
                  borderRadius: '12px',
                  border: `1px solid ${colors.border}`,
                  overflow: 'hidden'
                }}
              >
                {/* Template Header */}
                <div
                  style={{
                    padding: '1rem 1.25rem',
                    display: 'flex',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: '1rem',
                    cursor: 'pointer'
                  }}
                  onClick={() => setExpandedId(isExpanded ? null : template.id)}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <span style={{ color: colors.text, fontWeight: 'bold', fontSize: '1.05rem' }}>
                        {template.name}
                      </span>
                      {template.category && (
                        <span style={{
                          padding: '0.15rem 0.5rem',
                          background: colors.bgInput,
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          color: colors.textMuted
                        }}>
                          {template.category}
                        </span>
                      )}
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '1rem',
                      flexWrap: 'wrap',
                      color: colors.textMuted,
                      fontSize: '0.85rem'
                    }}>
                      <span>
                        <Clock size={14} style={{ marginRight: '0.25rem', verticalAlign: 'text-bottom' }} />
                        {template.estimated_duration}min
                      </span>
                      <span>
                        <Package size={14} style={{ marginRight: '0.25rem', verticalAlign: 'text-bottom' }} />
                        {itemCount} items
                      </span>
                      {totalMaterialsCost > 0 && (
                        <span>
                          <DollarSign size={14} style={{ marginRight: '0.25rem', verticalAlign: 'text-bottom' }} />
                          ${totalMaterialsCost.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditModal(template); }}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        color: colors.textMuted,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDuplicate(template); }}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        color: colors.textMuted,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(template); }}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        color: colors.error,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                    {isExpanded ? <ChevronUp size={20} color={colors.textMuted} /> : <ChevronDown size={20} color={colors.textMuted} />}
                  </div>
                </div>

                {/* Expanded Items */}
                {isExpanded && template.job_template_items?.length > 0 && (
                  <div style={{
                    borderTop: `1px solid ${colors.border}`,
                    padding: '1rem 1.25rem',
                    background: colors.bgInput
                  }}>
                    <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '0.5rem' }}>
                      Materials:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {template.job_template_items.map((item, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.5rem 0.75rem',
                          background: colors.bgCard,
                          borderRadius: '6px'
                        }}>
                          <span style={{ color: colors.text }}>
                            {item.quantity}x {item.name}
                            {item.is_optional && (
                              <span style={{ color: colors.textMuted, fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                                (optional)
                              </span>
                            )}
                          </span>
                          {item.unit_cost > 0 && (
                            <span style={{ color: colors.textMuted }}>
                              ${(item.quantity * item.unit_cost).toFixed(2)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: colors.bgCard,
              borderRadius: '16px',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h2 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
                {editingTemplate ? 'Edit Template' : 'Create Template'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
              {/* Name */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                  Template Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Standard Door Installation"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text
                  }}
                />
              </div>

              {/* Category & Duration */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.9rem' }}>
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem 2.25rem 0.75rem 0.75rem',
                      background: `${colors.bgInput} url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>") right 0.75rem center / 14px no-repeat`,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Select...</option>
                    {commonCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.9rem' }}>
                    Est. Duration (min)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.estimated_duration}
                    onChange={(e) => setFormData({ ...formData, estimated_duration: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text
                    }}
                  />
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.9rem' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional notes about this template"
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Materials */}
              <div style={{
                background: colors.bgInput,
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.75rem'
                }}>
                  <span style={{ color: colors.text, fontWeight: 'bold' }}>
                    Materials ({templateItems.length})
                  </span>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => setShowItemPicker(true)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: colors.accent,
                        border: 'none',
                        borderRadius: '6px',
                        color: '#fff',
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                    >
                      <Package size={14} />
                      Inventory
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowTemplatePicker(true)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: colors.bgCard,
                        border: `1px solid ${colors.accent}`,
                        borderRadius: '6px',
                        color: colors.accent,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                    >
                      <Layers size={14} />
                      Template
                    </button>
                    <button
                      type="button"
                      onClick={addManualItem}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        color: colors.text,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                    >
                      <Plus size={14} />
                      Manual
                    </button>
                  </div>
                </div>

                {templateItems.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '1rem', color: colors.textMuted }}>
                    No materials added yet
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {templateItems.map((item, idx) => (
                      <div key={item.id} style={{
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'center',
                        padding: '0.5rem',
                        background: colors.bgCard,
                        borderRadius: '6px'
                      }}>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateTemplateItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                          style={{
                            width: '60px',
                            padding: '0.5rem',
                            background: colors.bgInput,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '4px',
                            color: colors.text,
                            textAlign: 'center'
                          }}
                        />
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateTemplateItem(idx, 'name', e.target.value)}
                          placeholder="Item name"
                          style={{
                            flex: 1,
                            padding: '0.5rem',
                            background: colors.bgInput,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '4px',
                            color: colors.text
                          }}
                        />
                        <div style={{ position: 'relative', width: '80px' }}>
                          <span style={{
                            position: 'absolute',
                            left: '0.5rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: colors.textMuted,
                            fontSize: '0.85rem'
                          }}>$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_cost}
                            onChange={(e) => updateTemplateItem(idx, 'unit_cost', parseFloat(e.target.value) || 0)}
                            style={{
                              width: '100%',
                              padding: '0.5rem 0.5rem 0.5rem 1.25rem',
                              background: colors.bgInput,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '4px',
                              color: colors.text
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeTemplateItem(idx)}
                          style={{
                            padding: '0.5rem',
                            background: 'none',
                            border: 'none',
                            color: colors.error,
                            cursor: 'pointer'
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={saving}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: colors.accent,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1
                }}
              >
                {saving ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Item Picker Modal */}
      {showItemPicker && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '1rem'
          }}
          onClick={() => setShowItemPicker(false)}
        >
          <div
            style={{
              background: colors.bgCard,
              borderRadius: '16px',
              width: '100%',
              maxWidth: '400px',
              maxHeight: '70vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '1rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span style={{ color: colors.text, fontWeight: 'bold' }}>Select Item</span>
              <button
                onClick={() => setShowItemPicker(false)}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '1rem', borderBottom: `1px solid ${colors.border}` }}>
              <input
                type="text"
                placeholder="Search inventory..."
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text
                }}
              />
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '0.5rem' }}>
              {filteredInventoryItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: colors.textMuted }}>
                  {inventoryItems.length === 0 ? 'No inventory items' : 'No matches found'}
                </div>
              ) : (
                filteredInventoryItems.map(item => (
                  <button
                    key={item.id}
                    onClick={() => addItemToTemplate(item)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      marginBottom: '0.5rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span style={{ color: colors.text }}>{item.name}</span>
                    {item.unit_cost > 0 && (
                      <span style={{ color: colors.textMuted }}>${item.unit_cost.toFixed(2)}</span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Template Picker Modal (for nested templates) */}
      {showTemplatePicker && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '1rem'
          }}
          onClick={() => setShowTemplatePicker(false)}
        >
          <div
            style={{
              background: colors.bgCard,
              borderRadius: '16px',
              width: '100%',
              maxWidth: '450px',
              maxHeight: '70vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '1rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <span style={{ color: colors.text, fontWeight: 'bold' }}>Include Template</span>
                <p style={{ margin: '0.25rem 0 0', color: colors.textMuted, fontSize: '0.85rem' }}>
                  Add all items from another template
                </p>
              </div>
              <button
                onClick={() => setShowTemplatePicker(false)}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '1rem', borderBottom: `1px solid ${colors.border}` }}>
              <input
                type="text"
                placeholder="Search templates..."
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text
                }}
              />
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '0.5rem' }}>
              {filteredTemplatesForPicker.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: colors.textMuted }}>
                  {templates.length <= 1 ? 'No other templates available' : 'No templates match your search'}
                </div>
              ) : (
                filteredTemplatesForPicker.map(template => (
                  <button
                    key={template.id}
                    onClick={() => includeTemplate(template)}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      marginBottom: '0.5rem',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: colors.text, fontWeight: 'bold' }}>{template.name}</span>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        background: colors.accent + '20',
                        color: colors.accent,
                        borderRadius: '4px',
                        fontSize: '0.75rem'
                      }}>
                        {template.job_template_items?.length || 0} items
                      </span>
                    </div>
                    {template.category && (
                      <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginTop: '0.25rem' }}>
                        {template.category}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
